import { FormatLabel } from '../production/productionLabels';

/**
 * Núcleo NÃO-VISUAL da EXPEDIÇÃO (M8): leitura do embarque, do progresso do
 * carregamento e dos 409 que o console e o coletor precisam distinguir.
 *
 * ── A regra que este arquivo serve ──────────────────────────────────────────
 * **Nada fecha com item não bipado.** A trava é do BACKEND (motor puro
 * `shipmentChecklist.js`, conferido de novo dentro da transação de
 * fechamento) — aqui NÃO existe uma segunda versão dela. O que existe é a
 * leitura do que o servidor já decidiu (`can_close`, `close_blocks`,
 * `progress`), para a tela mostrar O QUE FALTA em vez de um botão cinza mudo.
 *
 * Recontar "quantos faltam" no navegador seria criar a segunda conta que um dia
 * discorda da primeira — e o dia em que ela discordar é o dia em que mercadoria
 * sai do galpão sem registro.
 */

/**
 * ⚠️ DUAS MUDANÇAS DE CONTRATO (dono, 13/08) que este arquivo carrega:
 *
 *   1. **um bipe por item** — o carregamento manda só `code`. `dock_code` saiu
 *      do corpo, e com ele o 409 de "doca errada": estar na doca voltou a ser a
 *      POSIÇÃO do pallet, registrada por derivação em `scanned_location_code`;
 *   2. **espelho em pedaços** — a fila do Bling tem uma linha por par
 *      (produto, depósito), e cada linha diz em qual depósito o espelho parou.
 */

/** Enums do contrato (espelho do backend — identidade, nunca comportamento). */
export const SHIPMENT_STATUSES = Object.freeze(['OPEN', 'CLOSED', 'CANCELED']);
export const SHIPMENT_ITEM_KINDS = Object.freeze(['LPN', 'VOLUME']);
export const SHIPMENT_ITEM_STATUSES = Object.freeze(['PENDING', 'LOADED']);
export const BLING_SYNC_STATUSES = Object.freeze(['PENDING', 'SENT', 'FAILED']);

/**
 * Códigos de erro do M8 que a UI TRATA um a um. Erro tratado por código é o que
 * separa "você bipou a carga do caminhão errado" de "deu erro" — e é a
 * diferença entre o operador corrigir sozinho e chamar o Supervisor.
 */
export const SHIPMENT_ERROR = Object.freeze({
    CLOSE_BLOCKED: 'WMS26_SHIPMENT_CLOSE_BLOCKED',
    NOT_IN_CHECKLIST: 'WMS26_SHIPMENT_ITEM_NOT_IN_CHECKLIST',
    ALREADY_LOADED: 'WMS26_SHIPMENT_ITEM_ALREADY_LOADED',
    NOT_OPEN: 'WMS26_SHIPMENT_NOT_OPEN',
    ALREADY_EXISTS: 'WMS26_SHIPMENT_ALREADY_EXISTS',
    OUTBOUND_CLOSED: 'WMS26_SHIPMENT_OUTBOUND_CLOSED',
    LPN_NOT_ACTIVE: 'WMS26_SHIPMENT_LPN_NOT_ACTIVE',
    LPN_ALREADY_LISTED: 'WMS26_SHIPMENT_LPN_ALREADY_LISTED',
    // ⚠️ `WMS26_SHIPMENT_DOCK_MISMATCH` MORREU em 13/08: o carregamento não bipa
    // mais a doca, então não existe mais "doca errada" para tratar. O código
    // saiu daqui de propósito — deixá-lo listado faria a próxima pessoa
    // procurar o caminho de UI que o produz e não achar nenhum.
});

/** Regras de bloqueio do fechamento, na ordem canônica do motor puro. */
export const CLOSE_BLOCK_RULES = Object.freeze(['NOT_OPEN', 'EMPTY_CHECKLIST', 'NOT_FULLY_SCANNED']);

const EMPTY_PROGRESS = Object.freeze({
    total: 0,
    loaded: 0,
    pending: 0,
    percent: 0,
    complete: false,
    pending_items: [],
});

function toInt(value) {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.trunc(number)) : 0;
}

/** Variante de badge por status do EMBARQUE. */
export function ShipmentStatusVariant(status) {
    if (status === 'OPEN') {
        return 'info';
    }
    if (status === 'CLOSED') {
        return 'success';
    }
    return 'neutral';
}

/** Variante de badge por status da LINHA do checklist. */
export function ShipmentItemStatusVariant(status) {
    return status === 'LOADED' ? 'success' : 'warning';
}

/** Variante de badge por status da linha da FILA do Bling. */
export function BlingSyncStatusVariant(status) {
    if (status === 'SENT') {
        return 'success';
    }
    if (status === 'FAILED') {
        return 'danger';
    }
    return 'warning';
}

/**
 * O progresso como o SERVIDOR o calculou. Nada é recontado aqui: `percent` é
 * apresentação e vem pronto (o motor usa CONTAGEM na trava justamente porque
 * 99,6% arredondado para 100 seria o furo que a regra proíbe).
 */
export function ParseShipmentProgress(shipment) {
    const progress = shipment?.progress;
    if (!progress || typeof progress !== 'object') {
        return EMPTY_PROGRESS;
    }
    const total = toInt(progress.total);
    const loaded = toInt(progress.loaded);
    return {
        total,
        loaded,
        pending: toInt(progress.pending ?? Math.max(0, total - loaded)),
        percent: toInt(progress.percent),
        complete: Boolean(progress.complete),
        pending_items: Array.isArray(progress.pending_items) ? progress.pending_items : [],
    };
}

/** Frase "X de Y bipados" — uma só, para lista, ficha e coletor não divergirem. */
export function FormatProgressText(progress, label) {
    return FormatLabel(label('wms.shipment.progress_of'), {
        loaded: toInt(progress?.loaded),
        total: toInt(progress?.total),
    });
}

function checklistLines(shipment) {
    return Array.isArray(shipment?.items) ? shipment.items : [];
}

/**
 * O QUE FALTA BIPAR — a lista que fica VISÍVEL o tempo todo.
 *
 * Preferência pelas linhas completas do checklist (`items`, que trazem tipo e
 * código); a LISTA de embarques vem sem elas de propósito (o backend zera
 * `items`/`pending_items` no resumo para não fazer N+1), e aí o retorno é
 * vazio — a ficha é quem sabe. Quem consome trata a lista vazia com o número do
 * progresso, nunca com "não falta nada".
 */
export function MissingChecklistLines(shipment) {
    const lines = checklistLines(shipment).filter((line) => line?.status !== 'LOADED');
    if (lines.length > 0) {
        return lines;
    }
    return ParseShipmentProgress(shipment).pending_items;
}

/** O que já entrou no veículo (a outra metade da conferência). */
export function LoadedChecklistLines(shipment) {
    return checklistLines(shipment).filter((line) => line?.status === 'LOADED');
}

/** Código do erro do backend, venha ele na raiz ou aninhado no envelope. */
export function ErrorCodeOf(error) {
    return error?.code ?? error?.details?.code ?? error?.details?.error?.code ?? null;
}

export function IsCloseBlockedError(error) {
    return ErrorCodeOf(error) === SHIPMENT_ERROR.CLOSE_BLOCKED;
}

export function IsNotInChecklistError(error) {
    return ErrorCodeOf(error) === SHIPMENT_ERROR.NOT_IN_CHECKLIST;
}

export function IsShipmentNotOpenError(error) {
    return ErrorCodeOf(error) === SHIPMENT_ERROR.NOT_OPEN;
}

function detailsOf(error) {
    return error?.details ?? error?.details?.error?.details ?? null;
}

/** Motivos do bloqueio: `[{rule, message}]`, do embarque OU do 409 do fechamento. */
export function ParseCloseBlocks(source) {
    const raw = Array.isArray(source?.close_blocks) ? source.close_blocks : detailsOf(source)?.blocks;
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .map((item, index) =>
            typeof item === 'string'
                ? { rule: `BLOCK_${index + 1}`, message: item }
                : { rule: item?.rule || `BLOCK_${index + 1}`, message: item?.message || '' },
        )
        .filter((item) => item.message);
}

/**
 * O 409 DA TRAVA, aberto: os motivos + o progresso FRESCO que o servidor viu no
 * instante do clique.
 *
 * Ele existe porque entre a tela e o botão cabe um item novo no checklist (o
 * fracionado do M7 continua criando volume enquanto o caminhão carrega). A tela
 * dizia 100% e o servidor, que reconfere com os dados do banco, diz que não —
 * e isso NÃO é falha da tela nem do operador: é a trava funcionando.
 */
export function ParseCloseBlockedError(error) {
    const details = detailsOf(error) || {};
    const progress = ParseShipmentProgress({ progress: details.progress });
    const blocks = ParseCloseBlocks(error);
    return {
        blocks,
        progress,
        // As linhas que faltam vêm do PRÓPRIO 409 (`{id, kind, code}`) — é o
        // retrato do servidor, não o que a tela tinha em memória.
        missing: progress.pending_items,
        rules: blocks.map((block) => block.rule),
    };
}

/**
 * Bipe repetido: o backend responde 200 com `scan.already_loaded = true` (a
 * linha já está `LOADED` e NENHUM movimento novo é gravado). Não é erro — na
 * doca, repetir bipe é rotina, e pintar isso de vermelho ensinaria o chão a
 * ignorar o vermelho.
 */
export function IsAlreadyLoadedScan(payload) {
    return Boolean(payload?.scan?.already_loaded);
}

/** Resumo do fechamento (`closed_summary`) em forma segura para a tela. */
export function ParseClosedSummary(payload) {
    const summary = payload?.closed_summary;
    if (!summary || typeof summary !== 'object') {
        return null;
    }
    return {
        lpns_closed: toInt(summary.lpns_closed),
        volumes_closed: toInt(summary.volumes_closed),
        writeoff_movements: toInt(summary.writeoff_movements),
        bling_sync_enqueued: toInt(summary.bling_sync_enqueued),
        deposit_rule: summary.deposit_rule ?? null,
        media_attached: toInt(payload?.media?.attached_now),
    };
}

/** Códigos de LPN de um textarea (uma por linha) — sem duplicatas, sem vazios. */
export function ParseLpnCodeList(text) {
    return [
        ...new Set(
            String(text ?? '')
                .split(/[\n,;]+/)
                .map((code) => code.replace(/\s+/g, '').toUpperCase())
                .filter(Boolean),
        ),
    ];
}

/** Rótulo curto do romaneio: o que o Supervisor reconhece na lista. */
export function FormatOutboundSummary(outbound) {
    if (!outbound) {
        return '';
    }
    const parts = [];
    if (outbound.freight_code) {
        parts.push(`Frete #${outbound.freight_code}`);
    }
    if (outbound.haulier_name) {
        parts.push(outbound.haulier_name);
    }
    if (outbound.vehicle_plate) {
        parts.push(outbound.vehicle_plate);
    }
    return parts.join(' · ');
}
